import type { AxiosInstance } from 'axios';

export type MobilePlatform = 'ANDROID' | 'IOS' | 'WEB_PUSH';
export type MobileAppRole = 'CUSTOMER' | 'VENDOR' | 'DELIVERY' | 'ADMIN';
export type MobileDeviceStatus = 'ACTIVE' | 'INACTIVE' | 'REVOKED';

export interface MobileDevice {
  id: string;
  userId: string;
  deviceId: string;
  appRole: MobileAppRole;
  platform: MobilePlatform;
  pushToken: string | null;
  appVersion: string | null;
  osVersion: string | null;
  model: string | null;
  label: string | null;
  status: MobileDeviceStatus;
  lastSeenAt: string;
  createdAt: string;
}

export interface RegisterDeviceRequest {
  deviceId: string;
  appRole: MobileAppRole;
  platform: MobilePlatform;
  pushToken?: string;
  appVersion?: string;
  osVersion?: string;
  model?: string;
  label?: string;
}

export type VersionGateAction = 'OK' | 'SOFT_UPDATE' | 'FORCE_UPDATE';

export interface VersionGateRequest {
  appRole: MobileAppRole;
  platform: MobilePlatform;
  currentVersion: string;
}

export interface VersionGateResult {
  action: VersionGateAction;
  minSupported: string;
  latestReleased: string;
  storeUrl: string | null;
  releaseNotes: string | null;
}

export function mobileEndpoints(http: AxiosInstance) {
  return {
    // The Idempotency-Key this endpoint requires is added by the request
    // interceptor. It used to be set here with `crypto.randomUUID()`, which
    // React Native's engine does not provide — the very client that registers
    // devices was the one that could not call this.
    registerDevice: (body: RegisterDeviceRequest) =>
      http.post<MobileDevice>('/mobile/devices', body).then((r) => r.data),

    updatePushToken: (body: { deviceId: string; pushToken: string }) =>
      http.post<MobileDevice>('/mobile/devices/push-token', body).then((r) => r.data),

    revokeDevice: (body: { deviceId: string }) =>
      http.post<MobileDevice>('/mobile/devices/revoke', body).then((r) => r.data),

    listMyDevices: () => http.get<MobileDevice[]>('/mobile/devices/me').then((r) => r.data),

    /**
     * Ask whether this build is still allowed to run.
     *
     * Unauthenticated on purpose: an app too old to talk to the API has to be
     * told so before it tries to sign anyone in.
     */
    checkVersion: (body: VersionGateRequest) =>
      http.post<VersionGateResult>('/mobile/app/version/check', body).then((r) => r.data),
  };
}

export type MobileEndpoints = ReturnType<typeof mobileEndpoints>;
